<template>
  <div class="fa-tenant-view">
    <div v-if="loading" style="padding: 20px; text-align: center;">
      <b-spinner/>
    </div>

    <template v-else>
      <!-- summary card -->
      <div class="fa-tenant-card">
        <div class="fa-tenant-card-row">
          <div class="fa-stat">
            <div class="fa-stat-label">Fleet Assistant</div>
            <div class="fa-stat-value">
              <span v-if="tenantData.fa_enabled" style="color: #28a745;"><i class="fa fa-check-circle"/> On</span>
              <span v-else style="color: #888;"><i class="fa fa-circle-o"/> Off</span>
            </div>
          </div>
          <div class="fa-stat">
            <div class="fa-stat-label">Monthly quota</div>
            <div class="fa-stat-value">{{ formatCents(tenantData.quota_limit_cents) }}</div>
          </div>
          <div class="fa-stat">
            <div class="fa-stat-label">Used this month</div>
            <div class="fa-stat-value">{{ formatCents(tenantData.blended_cost_cents) }}
              <span style="font-size: 13px; color: #888; font-weight: normal;">({{ tenantData.quota_pct }}%)</span></div>
          </div>
          <div class="fa-stat">
            <div class="fa-stat-label">Active users</div>
            <div class="fa-stat-value">{{ tenantData.active_users }}</div>
          </div>
          <div class="fa-stat">
            <div class="fa-stat-label">Chat / Docs</div>
            <div class="fa-stat-value">{{ tenantData.chat_requests || 0 }}<span style="font-size: 13px; color: #888; font-weight: normal;"> / {{ tenantData.doc_categorizations || 0 }}</span></div>
          </div>
          <div class="fa-stat" v-if="tenantData.reconciled_at">
            <div class="fa-stat-label">Last reconciled</div>
            <div class="fa-stat-value" style="font-size: 13px;">{{ formatEpoch(tenantData.reconciled_at) }}</div>
          </div>
        </div>

        <div class="fa-tenant-actions">
          <b-button size="sm" variant="outline-primary" @click="$emit('open-feedback', tenantData.tenant_id)">
            <i class="fa fa-comments-o" style="margin-right: 4px;"/>Feedback for this tenant
          </b-button>
        </div>
      </div>

      <!-- charts row -->
      <div class="fa-charts-row" v-if="historyRows.length > 0">
        <div class="fa-chart-card">
          <div class="fa-chart-title">12-month blended cost</div>
          <GChart
            type="AreaChart"
            :data="costTrendData"
            :options="areaOpts"
            :settings="{ packages: ['corechart'] }"/>
        </div>
        <div class="fa-chart-card">
          <div class="fa-chart-title">Chat vs. document cost</div>
          <GChart
            type="ColumnChart"
            :data="chatDocSplitData"
            :options="stackedColumnOpts"
            :settings="{ packages: ['corechart'] }"/>
        </div>
        <div class="fa-chart-card">
          <div class="fa-chart-title">Top models (this month, by tokens)</div>
          <GChart
            v-if="modelDonutData.length > 1"
            type="PieChart"
            :data="modelDonutData"
            :options="donutOpts"
            :settings="{ packages: ['corechart'] }"/>
          <div v-else style="padding: 30px; text-align: center; color: #888; font-size: 13px;">No model-level data yet.</div>
        </div>
      </div>

      <!-- users table -->
      <div class="fa-section">
        <div class="fa-section-title">Users this month</div>
        <b-table :items="tenantData.users || []" :fields="userFields" small striped hover responsive sort-icon-left>
          <template slot="user_id" slot-scope="row">
            <a href="#" @click.prevent="$emit('open-user', { tenant_id: tenantData.tenant_id, user_id: row.item.user_id })" class="fa-tenant-link">{{ row.item.user_id }}</a>
          </template>
          <template slot="blended_cost_cents" slot-scope="row">{{ formatCents(row.item.blended_cost_cents) }}</template>
          <template slot="input_tokens" slot-scope="row">{{ formatTokens(row.item.input_tokens) }}</template>
          <template slot="output_tokens" slot-scope="row">{{ formatTokens(row.item.output_tokens) }}</template>
        </b-table>
        <div v-if="(tenantData.users || []).length === 0" style="color: #888; font-size: 13px; padding: 10px;">No per-user activity this month.</div>
      </div>

      <!-- tool call summary (scoped to the current month to match the
           "this month" framing on chat_requests above). -->
      <div class="fa-section">
        <div class="fa-section-title">Tool calls this month ({{ tenantData.tools_summary && tenantData.tools_summary.session_count || 0 }} sessions · {{ tenantData.tools_summary && tenantData.tools_summary.turn_count || 0 }} turns)</div>
        <div v-if="tenantData.tools_summary && tenantData.tools_summary.truncated" class="fa-tools-trunc">
          <i class="fa fa-info-circle" style="margin-right: 4px;"/>
          Scan capped at {{ tenantData.tools_summary.item_cap }} sessions — numbers below are a floor, not a total.
        </div>
        <b-table v-if="(tenantData.tools_summary && tenantData.tools_summary.tools && tenantData.tools_summary.tools.length) > 0"
                 :items="tenantData.tools_summary.tools"
                 :fields="toolFields"
                 small striped hover responsive sort-icon-left/>
        <div v-else style="color: #888; font-size: 13px; padding: 10px;">No tool calls recorded this month.</div>
      </div>

      <!-- documents -->
      <div class="fa-section">
        <div class="fa-section-title">Company documents ({{ (tenantData.documents || []).length }})</div>
        <b-table v-if="(tenantData.documents || []).length > 0"
                 :items="tenantData.documents"
                 :fields="docFields"
                 small striped hover responsive sort-icon-left>
          <template slot="file_size" slot-scope="row">{{ formatBytes(row.item.file_size) }}</template>
          <template slot="uploaded_at" slot-scope="row">{{ formatEpoch(row.item.uploaded_at) }}</template>
        </b-table>
        <div v-else style="color: #888; font-size: 13px; padding: 10px;">No company documents.</div>
      </div>
    </template>
  </div>
</template>

<script>
import { GChart } from 'vue-google-charts'
import { formatCents, formatEpoch, formatTokens } from '../../helpers/fa_admin'

export default {
  name: 'FaTenantView',
  components: { GChart },
  props: {
    tenantData: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: false },
  },
  computed: {
    historyRows () {
      return (this.tenantData && this.tenantData.history) || []
    },
    costTrendData () {
      const rows = [...this.historyRows].sort((a, b) => a.period.localeCompare(b.period))
      const header = ['Month', 'Blended cost ($)']
      const data = rows.map(r => [r.period, Math.round((r.blended_cost_cents || 0)) / 100])
      return [header, ...data]
    },
    chatDocSplitData () {
      const rows = [...this.historyRows].sort((a, b) => a.period.localeCompare(b.period))
      const header = ['Month', 'Chat ($)', 'Docs ($)']
      const data = rows.map(r => [
        r.period,
        Math.round((r.chat_estimated_cost_cents || 0)) / 100,
        Math.round((r.doc_estimated_cost_cents || 0)) / 100,
      ])
      return [header, ...data]
    },
    modelDonutData () {
      // derive top 5 models by (input + output) tokens from the current row
      const latest = [...this.historyRows].sort((a, b) => b.period.localeCompare(a.period))[0]
      if (!latest) return [['Model', 'Tokens']]
      const buckets = []
      Object.keys(latest).forEach(k => {
        if (!k.startsWith('mt_') || (!k.endsWith('_in') && !k.endsWith('_out'))) return
        const slug = k.replace(/^mt_/, '').replace(/_(in|out)$/, '')
        let existing = buckets.find(b => b.slug === slug)
        if (!existing) {
          existing = { slug, tokens: 0 }
          buckets.push(existing)
        }
        existing.tokens += Number(latest[k]) || 0
      })
      buckets.sort((a, b) => b.tokens - a.tokens)
      const top = buckets.slice(0, 5)
      if (top.length === 0) return [['Model', 'Tokens']]
      return [['Model', 'Tokens'], ...top.map(b => [b.slug.replace(/_/g, '-'), b.tokens])]
    },
    // google charts draw text in svg from js options, so css tokens can't
    // reach them. null on light themes so the defaults stay untouched;
    // midnight injects a light ink into the text styles below.
    chart_ink () {
      return this.$store.state.profile.site_theme === 'midnight' ? '#d7e1ea' : null
    },
    chart_text_style () {
      return this.chart_ink ? { color: this.chart_ink } : {}
    },
    areaOpts () {
      return {
        height: 220,
        legend: { position: 'none' },
        backgroundColor: { fill: 'transparent' },
        colors: ['#007ac7'],
        chartArea: { left: 50, top: 15, width: '85%', height: '75%' },
        hAxis: { textStyle: { fontSize: 10, ...this.chart_text_style } },
        vAxis: { textStyle: { fontSize: 10, ...this.chart_text_style }, format: '$#' },
      }
    },
    stackedColumnOpts () {
      return {
        height: 220,
        legend: { position: 'top', textStyle: { fontSize: 11, ...this.chart_text_style } },
        backgroundColor: { fill: 'transparent' },
        isStacked: true,
        colors: ['#007ac7', '#f0a957'],
        chartArea: { left: 50, top: 30, width: '85%', height: '70%' },
        hAxis: { textStyle: { fontSize: 10, ...this.chart_text_style } },
        vAxis: { textStyle: { fontSize: 10, ...this.chart_text_style }, format: '$#' },
      }
    },
    donutOpts () {
      return {
        height: 220,
        legend: { position: 'right', textStyle: { fontSize: 11, ...this.chart_text_style } },
        backgroundColor: { fill: 'transparent' },
        pieHole: 0.5,
        chartArea: { left: 10, top: 15, width: '95%', height: '85%' },
        colors: ['#007ac7', '#f0a957', '#28a745', '#a062c0', '#888888'],
      }
    },
    userFields () {
      return [
        { key: 'user_id', label: 'User', sortable: true },
        { key: 'blended_cost_cents', label: 'Cost', sortable: true, thStyle: { width: '100px' } },
        { key: 'chat_requests', label: 'Chats', sortable: true, thStyle: { width: '80px' } },
        { key: 'doc_categorizations', label: 'Docs', sortable: true, thStyle: { width: '80px' } },
        { key: 'input_tokens', label: 'In tokens', sortable: true, thStyle: { width: '100px' } },
        { key: 'output_tokens', label: 'Out tokens', sortable: true, thStyle: { width: '100px' } },
      ]
    },
    toolFields () {
      return [
        { key: 'name', label: 'Tool', sortable: true },
        { key: 'count', label: 'Count', sortable: true, thStyle: { width: '80px' } },
        { key: 'avg_duration_ms', label: 'Avg (ms)', sortable: true, thStyle: { width: '100px' } },
        { key: 'failure_count', label: 'Failures', sortable: true, thStyle: { width: '90px' } },
        {
          key: 'last_seen',
          label: 'Last seen',
          sortable: true,
          thStyle: { width: '160px' },
          formatter: (v) => v ? formatEpoch(v) : '—',
        },
      ]
    },
    docFields () {
      return [
        { key: 'display_name', label: 'Name', sortable: true },
        { key: 'doc_type', label: 'Type', sortable: true, thStyle: { width: '140px' } },
        { key: 'audience', label: 'Audience', sortable: true, thStyle: { width: '140px' } },
        { key: 'file_size', label: 'Size', sortable: true, thStyle: { width: '100px' } },
        { key: 'uploaded_by', label: 'Uploaded by', sortable: true, thStyle: { width: '160px' } },
        { key: 'uploaded_at', label: 'When', sortable: true, thStyle: { width: '160px' } },
      ]
    },
  },
  methods: {
    formatCents,
    formatEpoch,
    formatTokens,
    formatBytes (bytes) {
      const b = Number(bytes || 0)
      if (!isFinite(b) || b <= 0) return '—'
      if (b >= 1024 * 1024) return (b / (1024 * 1024)).toFixed(1) + ' MB'
      if (b >= 1024) return (b / 1024).toFixed(1) + ' kB'
      return b + ' B'
    },
  },
}
</script>

<style scoped>
.fa-tenant-view { padding: 8px 0; }

.fa-tenant-card {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
}
.fa-tenant-card-row {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
}
.fa-stat { min-width: 130px; }
.fa-stat-label {
  font-size: 11px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.fa-stat-value {
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin-top: 2px;
}
.fa-tenant-actions {
  margin-top: 12px;
  display: flex;
  gap: 8px;
}

.fa-charts-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 14px;
  margin-bottom: 16px;
}
.fa-chart-card {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 10px 12px;
}
.fa-chart-title {
  font-size: 13px;
  font-weight: bold;
  color: #555;
  margin-bottom: 4px;
}

.fa-section {
  margin-top: 20px;
}
.fa-section-title {
  font-size: 15px;
  font-weight: bold;
  color: var(--fi-rail-accent);
  margin-bottom: 6px;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 4px;
}

.fa-tenant-link {
  color: var(--fi-brand);
  font-weight: 600;
  text-decoration: none;
}
.fa-tenant-link:hover { text-decoration: underline; }

/* canonical b-table treatment from support_ticket_list (see
   standard_table_pattern.md): 13px body / 12px header, dense 3px 8px
   padding, horizontal separators only, uppercase muted sticky header,
   steelblue sort arrows, soft #f0f5ff hover row. covers all three
   b-tables in this view (users, tool calls, documents) so they read
   as one consistent pattern with the FA Fleet table. `>>>` deep
   selectors needed because BV2 renders `.table.b-table` / `thead th`
   etc. internally and they don't pick up the scoped-CSS data-v-HASH
   attribute on their own. */
.fa-tenant-view >>> .table.b-table {
  font-size: 13px;
  border-collapse: collapse;
  margin-bottom: 0;
}
.fa-tenant-view >>> .table.b-table th,
.fa-tenant-view >>> .table.b-table td {
  border-left: none !important;
  border-right: none !important;
  border-bottom: 1px solid #e0e0e0;
  padding: 3px 8px !important;
  vertical-align: middle !important;
  text-align: left !important;
}
.fa-tenant-view >>> .table.b-table th:first-child,
.fa-tenant-view >>> .table.b-table td:first-child { padding-left: 12px !important; }
.fa-tenant-view >>> .table.b-table th:last-child,
.fa-tenant-view >>> .table.b-table td:last-child { padding-right: 12px !important; }
.fa-tenant-view >>> .table.b-table thead th {
  background: #f8f9fa;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #555;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 2;
  box-shadow: inset 0 -2px 0 0 #ccc;
}
.fa-tenant-view >>> .table.b-table tbody tr:hover td { background-color: #f0f5ff; }
.fa-tenant-view >>> th[aria-sort] {
  background-image: none !important;
  padding-right: 0.75rem !important;
  white-space: nowrap;
}
.fa-tenant-view >>> th[aria-sort]::before { display: none !important; }
.fa-tenant-view >>> th[aria-sort]::after {
  display: inline-block !important;
  margin-left: 4px;
  font-size: 10px;
  color: #999;
  vertical-align: middle;
  float: none !important;
  width: auto !important;
  height: auto !important;
  opacity: 1 !important;
}
.fa-tenant-view >>> th[aria-sort="none"]::after { content: "\2195" !important; }
.fa-tenant-view >>> th[aria-sort="ascending"]::after { content: "\25B2" !important; color: var(--fi-rail-accent); }
.fa-tenant-view >>> th[aria-sort="descending"]::after { content: "\25BC" !important; color: var(--fi-rail-accent); }

.fa-tools-trunc {
  font-size: 12px;
  color: #8a6100;
  background: #fff8ef;
  border: 1px solid #f0d5a8;
  border-radius: 4px;
  padding: 6px 10px;
  margin-bottom: 8px;
}
</style>
